import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { STAFF_ROLE_LABEL, type StaffMe } from '@reikraft/shared';
import { cx, Icon, type IconName, Loader, LogoContent } from '@reikraft/ui';
import { api, ApiError, errorText } from './api';
import { SessionProvider } from './session';

interface Counters {
  inWork: number;
  requests: number;
  orgLeads: number;
  receiptsPending: number;
}

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  badge?: (c: Counters) => { n: number; outline?: boolean; suffix?: string } | null;
  show?: (me: StaffMe) => boolean;
}

const NAV: NavItem[] = [
  { to: '/orders', label: 'Заказы', icon: 'orders', badge: (c) => (c.inWork ? { n: c.inWork } : null) },
  {
    to: '/intake',
    label: 'Приёмка',
    icon: 'intake',
    badge: (c) => (c.requests ? { n: c.requests, outline: true, suffix: ' нов.' } : null),
  },
  { to: '/stock', label: 'Склад и витрина', icon: 'stock' },
  { to: '/clients', label: 'Клиенты', icon: 'clients' },
  {
    to: '/organizations',
    label: 'Организации',
    icon: 'orgs',
    badge: (c) => (c.orgLeads ? { n: c.orgLeads, outline: true, suffix: ' нов.' } : null),
  },
  { to: '/masters', label: 'Мастера', icon: 'masters', show: (me) => me.flags.masters },
  { to: '/parts', label: 'Запчасти', icon: 'qr', show: (me) => me.flags.inventory },
  {
    to: '/finance',
    label: 'Финансы',
    icon: 'finance',
    badge: (c) => (c.receiptsPending ? { n: c.receiptsPending, outline: true, suffix: ' чек.' } : null),
  },
  { to: '/settings', label: 'Настройки', icon: 'settings' },
];

const MOBILE_MAIN = ['/orders', '/stock', '/clients'];

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
}

export function Shell() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<StaffMe>('/admin/auth/me'), retry: false, staleTime: 60_000 });
  const counters = useQuery({
    queryKey: ['counters'],
    queryFn: () => api.get<Counters>('/admin/counters'),
    enabled: me.isSuccess,
    refetchInterval: 60_000,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [more, setMore] = useState(false);

  useEffect(() => setMore(false), [location.pathname]);

  if (me.isLoading) return <Loader />;
  if (me.error instanceof ApiError && me.error.status === 401) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (!me.data) return <div className="rk-empty">Не удалось загрузить данные: {errorText(me.error)}</div>;

  const items = NAV.filter((n) => !n.show || n.show(me.data));
  const c = counters.data;
  const logout = async () => {
    await api.post('/admin/auth/logout');
    qc.clear();
    void navigate('/login');
  };

  return (
    <SessionProvider me={me.data}>
      <div className="adm">
        <aside className="adm-side">
          <Link to="/orders" className="rk-logo rk-logo--compact adm-side__logo" aria-label="Рейкрафт — заказы">
            <LogoContent brand={me.data.company.brand} compact />
            <span className="adm-badge">ADMIN</span>
          </Link>
          <nav aria-label="Разделы админки" className="adm-nav">
            {items.map((n) => {
              const b = c && n.badge?.(c);
              return (
                <NavLink key={n.to} to={n.to} className={({ isActive }) => cx('adm-nav__item', isActive && 'is-active')}>
                  <Icon name={n.icon} />
                  {n.label}
                  {b && (
                    <span className={cx('rk-count', b.outline && 'rk-count--outline')} style={{ marginLeft: 'auto' }}>
                      {b.n}
                      {b.suffix}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>
          <div className="adm-user">
            <span className="adm-user__avatar">{initials(me.data.name)}</span>
            <span className="adm-user__name">
              <b>{me.data.name}</b>
              <span>{STAFF_ROLE_LABEL[me.data.role]}</span>
            </span>
            <button
              type="button"
              className="rk-btn rk-btn--sm rk-btn--icon"
              aria-label="Выйти"
              title="Выйти"
              onClick={logout}
              style={{ marginLeft: 'auto', borderColor: 'transparent' }}
            >
              <Icon name="logout" />
            </button>
          </div>
        </aside>

        <main className="adm-main">
          <Outlet />
        </main>

        <nav aria-label="Нижнее меню" className="adm-bottom">
          {items
            .filter((n) => MOBILE_MAIN.includes(n.to))
            .map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => cx('adm-bottom__item', isActive && 'is-active')}>
                <Icon name={n.icon} size={22} />
                {n.to === '/stock' ? 'Склад' : n.label}
                {n.to === '/orders' && c?.requests ? <span className="adm-bottom__dot" aria-label={`новых заявок: ${c.requests}`} /> : null}
              </NavLink>
            ))}
          <button
            type="button"
            className={cx('adm-bottom__item', more && 'is-active')}
            aria-expanded={more}
            onClick={() => setMore((v) => !v)}
          >
            <Icon name="more" size={22} />
            Ещё
          </button>
        </nav>
        {more && (
          <div className="adm-sheet" role="dialog" aria-label="Все разделы">
            {items
              .filter((n) => !MOBILE_MAIN.includes(n.to))
              .map((n) => (
                <NavLink key={n.to} to={n.to} className="adm-nav__item">
                  <Icon name={n.icon} />
                  {n.label}
                </NavLink>
              ))}
            <button type="button" className="adm-nav__item" onClick={logout}>
              <Icon name="logout" />
              Выйти
            </button>
          </div>
        )}
      </div>
    </SessionProvider>
  );
}
